import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth/types';
import BooleanBadge from 'src/features/membership/components/BooleanBadge';
import { useListValidatorAccounts } from 'src/features/validatorAccounts/api/useListValidatorAccounts';
import CreateValidatorAccountFormModal from 'src/features/validatorAccounts/components/forms/CreateValidatorAccountFormModal';
import CellValidatorAccountsActions from 'src/features/validatorAccounts/components/tables/CellValidatorAccountActions';
import { validatorAccountsMessages } from 'src/features/validatorAccounts/utils/messages';
import { ValidatorAccount } from 'src/features/validatorAccounts/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { Button } from 'src/registry/ui/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'src/registry/ui/DropdownMenu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { generalMessages } from 'src/registry/utils/messages';
import { useTitleStore } from 'src/stores/useTitleStore';
import { cn } from 'src/utils';
import { booleanBadgeOptions, ICON_MAP } from 'src/utils/constants';

function ValidatorAccountsTable() {
    const { formatMessage } = useIntl();

    const manageValidations = useHasPermissions([PERMISSION.MANAGE_VALIDATIONS]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { parkingId } = useParking();

    const payload = React.useMemo(
        () => ({
            parkingId,
            ...querySearchOptions,
            ...queryFiltersOptions,
            ...querySortingOptions,
            ...queryPaginationOptions,
        }),
        [querySearchOptions, queryFiltersOptions, querySortingOptions, queryPaginationOptions, parkingId],
    );

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(validatorAccountsMessages.validationAccounts));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: validatorAccounts, isLoading } = useListValidatorAccounts(payload);

    const columnHelper = createColumnHelper<ValidatorAccount>();

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const descriptionLength = row.original.description?.length ?? 0;

                return (
                    <div>
                        <div className="font-medium">{row.original.name}</div>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className={cn(descriptionLength > 36 && 'max-w-64 cursor-default truncate')}>
                                        {row.original.description}
                                    </div>
                                </TooltipTrigger>
                                {descriptionLength > 36 && (
                                    <TooltipContent className="max-w-64 p-4">
                                        <div>{row.original.description}</div>
                                    </TooltipContent>
                                )}
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                );
            },
            meta: {
                title: formatMessage(generalMessages.name),
            },
            id: 'name',
        }),
        columnHelper.accessor('users.name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                const usersCount = ctx.row.original.users.length;

                const firstTwoUsers = ctx.row.original.users
                    ?.slice(0, 2)
                    ?.map((u) => u.name)
                    .join(', ');

                const remainingUsers = ctx.row.original.users.slice(2);

                return (
                    <div>
                        {usersCount <= 3 ? (
                            <div className="font-medium whitespace-break-spaces">
                                {ctx.row.original.users.map((u) => u.name).join(', ')}
                            </div>
                        ) : (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        className="cursor-pointer p-0 hover:bg-transparent hover:text-inherit hover:shadow-none"
                                    >
                                        <div>{firstTwoUsers}</div>
                                        <FontAwesomeIcon icon={ICON_MAP.ELLIPSIS} />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-full min-w-[200px]">
                                    <DropdownMenuLabel className="text-xs font-medium text-gray-600">
                                        {formatMessage(validatorAccountsMessages.users)}
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuGroup>
                                        {remainingUsers?.map((user, index) => {
                                            return (
                                                <span
                                                    key={index}
                                                    className="relative flex items-center rounded-xs px-2 py-1.5 text-sm outline-hidden transition-colors"
                                                >
                                                    {user.name}
                                                </span>
                                            );
                                        })}
                                    </DropdownMenuGroup>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                        <div>{`${usersCount} ${formatMessage(validatorAccountsMessages.total)}`}</div>
                    </div>
                );
            },
            meta: {
                title: formatMessage(validatorAccountsMessages.users),
            },
        }),
        columnHelper.accessor('cloud_users.name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                const cloudUsersCount = ctx.row.original.cloud_users.length;

                const firstTwoCloudUsers = ctx.row.original.cloud_users
                    ?.slice(0, 2)
                    ?.map((u) => u.name)
                    .join(', ');

                const remainingCloudUsers = ctx.row.original.cloud_users.slice(2);

                return (
                    <div>
                        {cloudUsersCount <= 3 ? (
                            <div className="font-medium whitespace-break-spaces">
                                {ctx.row.original.cloud_users.map((u) => u.name).join(', ')}
                            </div>
                        ) : (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        className="cursor-pointer p-0 hover:bg-transparent hover:text-inherit hover:shadow-none"
                                    >
                                        <div>{firstTwoCloudUsers}</div>
                                        <FontAwesomeIcon icon={ICON_MAP.ELLIPSIS} />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-full min-w-[200px]">
                                    <DropdownMenuLabel className="text-xs font-medium text-gray-600">
                                        {formatMessage(validatorAccountsMessages.cloudUsers)}
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuGroup>
                                        {remainingCloudUsers?.map((user, index) => {
                                            return (
                                                <span
                                                    key={index}
                                                    className="relative flex items-center rounded-xs px-2 py-1.5 text-sm outline-hidden transition-colors"
                                                >
                                                    {user.name}
                                                </span>
                                            );
                                        })}
                                    </DropdownMenuGroup>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                        <div>{`${cloudUsersCount} ${formatMessage(validatorAccountsMessages.total)}`}</div>
                    </div>
                );
            },
            meta: {
                title: formatMessage(validatorAccountsMessages.cloudUsers),
            },
        }),
        columnHelper.accessor('validation_types_count', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const typesCount = row.original.validation_types_count ?? 0;

                return formatMessage(validatorAccountsMessages.validationTypesCount, { count: typesCount });
            },
            meta: {
                title: formatMessage(validatorAccountsMessages.validationTypes),
            },
            maxSize: 140,
        }),
        columnHelper.accessor('is_system', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableColumnFilter: true,
            enableHiding: true,
            cell: ({ row }) => <BooleanBadge booleanField={row.original.is_system} />,
            meta: {
                title: formatMessage(validatorAccountsMessages.isSystem),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellValidatorAccountsActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<ValidatorAccount, any>
            isLoading={isLoading}
            columns={columns}
            data={validatorAccounts}
            search={search}
            filters={filters}
            sorting={sorting}
            pagination={pagination}
            semantics={{
                title: formatMessage(validatorAccountsMessages.validationAccounts),
                entryName: formatMessage(validatorAccountsMessages.account).toLowerCase(),
                entryNames: formatMessage(validatorAccountsMessages.accounts).toLowerCase(),
                placeholder: formatMessage(generalMessages.name),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(manageValidations && { newEntry: CreateValidatorAccountFormModal }),
            }}
        />
    );
}

export default ValidatorAccountsTable;
